import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { CheckCircle } from 'lucide-react';
import { interviewApi, interviewerApi, InterviewConfig } from '../../services/api';
import { InterviewRequestItem, InterviewerItem } from '../../types';
import { INTERVIEW_TECHNOLOGIES } from '../../content/interviewsPage';
import { useAuth } from '../../context/AuthContext';
import { canManageInterviews } from '../../utils/roles';
import { getUserProfileId } from '../../utils/userProfile';
import { confirmAction } from '../../utils/confirmAction';
import { formatDate } from '../../utils/formatDate';
import Button from '../../components/UI/Button';
import Card from '../../components/UI/Card';
import Input from '../../components/UI/Input';
import { PageLoading } from '../../components/UI/LoadingOverlay';

const interviewSchema = z
  .object({
    technology: z.string().min(1, 'Please select a technology'),
    otherTechnology: z.string().optional(),
    yearsOfExperience: z
      .number({ invalid_type_error: 'Years of experience is required' })
      .min(0, 'Enter a valid number')
      .max(50, 'Enter a valid number'),
    experienceDetails: z.string().min(10, 'Please describe your relevant experience'),
    role: z.string().min(2, 'Current or desired role is required'),
    roleDetails: z.string().min(10, 'Please add role details'),
    linkedInUrl: z
      .string()
      .url('Please enter a valid URL')
      .refine((value) => value.toLowerCase().includes('linkedin.com'), {
        message: 'Please enter a LinkedIn URL',
      }),
    additionalNotes: z.string().optional(),
  })
  .refine(
    (data) =>
      data.technology !== 'Other' ||
      (data.otherTechnology && data.otherTechnology.trim().length > 1),
    {
      message: 'Please specify the technology',
      path: ['otherTechnology'],
    }
  );

type InterviewFormData = z.infer<typeof interviewSchema>;

const fieldClasses = `block w-full rounded-md border-2 border-gray-300 shadow-sm bg-white text-gray-900
  placeholder:text-gray-500 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 focus:ring-opacity-20
  px-4 py-2.5 text-base`;
const labelClasses = 'block text-sm font-semibold text-gray-900 mb-1.5';

const InterviewDetailPage: React.FC = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [item, setItem] = useState<InterviewRequestItem | null>(null);
  const [config, setConfig] = useState<InterviewConfig | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [assigned, setAssigned] = useState(false);
  const [interviewers, setInterviewers] = useState<InterviewerItem[]>([]);
  const [meetingLink, setMeetingLink] = useState('');
  const [scheduledDate, setScheduledDate] = useState('');
  const [scheduledTime, setScheduledTime] = useState('');

  const {
    register,
    handleSubmit,
    reset,
    watch,
    formState: { errors },
  } = useForm<InterviewFormData>({
    resolver: zodResolver(interviewSchema),
  });

  const technology = watch('technology');
  const interviewId = Number(id);
  const technologies = config?.technologies?.length ? config.technologies : [...INTERVIEW_TECHNOLOGIES];
  const isOwnerOfRequest = item != null && getUserProfileId(user) === item.userProfileId;
  const isAdminEditor = canManageInterviews(user);
  const canEdit = !!item && item.status !== 'Cancelled' && (isAdminEditor || (item.status !== 'Completed' && isOwnerOfRequest));
  const canFeedback = !!item && item.status !== 'Cancelled' && (assigned || isAdminEditor);

  const reload = async () => {
    if (!interviewId) return;
    const [detail, assignedRes, configRes] = await Promise.all([
      interviewApi.getById(interviewId),
      interviewApi.getAssigned().catch(() => ({ data: { interviewRequests: [] as InterviewRequestItem[] } })),
      interviewApi.getConfig().catch(() => ({ data: { technologies: [...INTERVIEW_TECHNOLOGIES] } as InterviewConfig })),
    ]);
    if (!detail.data.success || !detail.data.interviewRequest) {
      throw new Error(detail.data.message || 'Interview not found.');
    }
    const next = detail.data.interviewRequest;
    setItem(next);
    setFeedback(next.feedback || '');
    setMeetingLink(next.meetingLink || '');
    setScheduledDate(next.scheduledDate ? next.scheduledDate.substring(0, 10) : '');
    setScheduledTime(next.scheduledTime || '');
    setAssigned((assignedRes.data.interviewRequests || []).some((row) => row.id === next.id));
    if (canManageInterviews(user)) {
      interviewerApi.list().then((r) => setInterviewers(r.data.interviewers || [])).catch(() => undefined);
    }
    setConfig(configRes.data as InterviewConfig);
    reset({
      technology: next.technology,
      otherTechnology: next.otherTechnology || '',
      yearsOfExperience: Number(next.yearsOfExperience),
      experienceDetails: next.experienceDetails,
      role: next.role,
      roleDetails: next.roleDetails,
      linkedInUrl: next.linkedInUrl,
      additionalNotes: next.additionalNotes || '',
    });
  };

  useEffect(() => {
    reload().catch((err: unknown) => setError(err instanceof Error ? err.message : 'Interview not found.'));
  }, [interviewId]);

  const onSave = async (data: InterviewFormData) => {
    if (!item) return;
    setSaving(true);
    setError(null);
    try {
      const response = await interviewApi.update(item.id, {
        technology: data.technology,
        otherTechnology: data.otherTechnology,
        yearsOfExperience: data.yearsOfExperience,
        experienceDetails: data.experienceDetails,
        role: data.role,
        roleDetails: data.roleDetails,
        linkedInUrl: data.linkedInUrl,
        additionalNotes: data.additionalNotes,
      });
      if (!response.data.success) {
        setError(response.data.message || 'Unable to save interview details.');
        return;
      }
      setMessage('Interview details saved.');
      await reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unable to save interview details.');
    } finally {
      setSaving(false);
    }
  };

  const onFeedback = async (markCompleted: boolean) => {
    if (!item) return;
    setSaving(true);
    setError(null);
    try {
      const response = await interviewApi.feedback(item.id, { feedback, markCompleted });
      if (!response.data.success) {
        setError(response.data.message || 'Unable to save feedback.');
        return;
      }
      setMessage(response.data.message || 'Feedback saved.');
      await reload();
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Unable to save feedback.');
    } finally {
      setSaving(false);
    }
  };

  if (error && !item) {
    return <div className="max-w-3xl mx-auto px-4 py-16 p-3 rounded-md bg-error-50 text-error-600 border border-error-200">{error}</div>;
  }
  if (!item) return <PageLoading label="Loading interview…" />;

  return (
    <div className="max-w-3xl mx-auto px-4 py-10">
      <div className="surface-card p-6 sm:p-8">
      <Link to="/dashboard?tab=interviews" className="text-sm font-medium text-primary-700 hover:text-primary-800">← Interviews</Link>
      <h1 className="mt-4 text-3xl font-bold text-gray-900">{item.otherTechnology || item.technology} interview</h1>
      <p className="mt-2 text-gray-600 flex flex-wrap items-center gap-2">
        {item.status === 'Completed' && <CheckCircle className="h-5 w-5 text-emerald-600" />}
        Status: <span className={`font-semibold ${item.status === 'Completed' ? 'text-emerald-700' : ''}`}>{item.status}</span>
        {item.scheduledDate ? ` · ${formatDate(item.scheduledDate)}${item.scheduledTime ? ` ${item.scheduledTime}` : ''}` : ''}
        {item.interviewerName ? ` · ${item.interviewerName}` : ''}
      </p>
      <Card className="mt-6 p-6">
        <h2 className="text-lg font-semibold text-gray-900">People</h2>
        <div className="mt-3 grid md:grid-cols-2 gap-4 text-sm text-gray-700">
          <div>
            <p className="font-semibold text-gray-900">Candidate</p>
            <p>{item.candidateName || item.userProfileId}</p>
            {item.candidateEmail && <p>{item.candidateEmail}</p>}
            {item.candidateMobile && <p>{item.candidateMobile}</p>}
            {(item.candidateLinkedInUrl || item.linkedInUrl) && (
              <a className="text-primary-700 hover:underline" href={item.candidateLinkedInUrl || item.linkedInUrl} target="_blank" rel="noreferrer">LinkedIn</a>
            )}
          </div>
          <div>
            <p className="font-semibold text-gray-900">Interviewer</p>
            <p>{item.interviewerName || 'Not assigned'}</p>
            {item.interviewerEmail && <p>{item.interviewerEmail}</p>}
            {item.interviewerPhone && <p>{item.interviewerPhone}</p>}
            {item.interviewerLinkedInUrl && (
              <a className="text-primary-700 hover:underline" href={item.interviewerLinkedInUrl} target="_blank" rel="noreferrer">LinkedIn</a>
            )}
          </div>
        </div>
      </Card>
      {message && <p className="mt-4 p-3 rounded-md bg-primary-50 text-primary-800 border border-primary-200">{message}</p>}
      {error && item && <p className="mt-4 p-3 rounded-md bg-error-50 text-error-600 border border-error-200">{error}</p>}

      <Card className="mt-6 p-6">
        <form onSubmit={handleSubmit(onSave)} className="space-y-4">
          <label className={labelClasses}>Technology
            <select className={`${fieldClasses} mt-1.5`} disabled={!canEdit} {...register('technology')}>
              {technologies.map((tech) => <option key={tech} value={tech}>{tech}</option>)}
            </select>
          </label>
          {technology === 'Other' && (
            <Input id="otherTechnology" label="Specify technology" disabled={!canEdit} error={errors.otherTechnology?.message} {...register('otherTechnology')} />
          )}
          <Input
            id="yearsOfExperience"
            type="number"
            step="0.1"
            label="Years of experience"
            disabled={!canEdit}
            error={errors.yearsOfExperience?.message}
            {...register('yearsOfExperience', { valueAsNumber: true })}
          />
          <label className={labelClasses}>Experience details
            <textarea className={`${fieldClasses} mt-1.5`} rows={4} disabled={!canEdit} {...register('experienceDetails')} />
            {errors.experienceDetails && <p className="mt-1 text-sm text-error-600">{errors.experienceDetails.message}</p>}
          </label>
          <Input id="role" label="Role" disabled={!canEdit} error={errors.role?.message} {...register('role')} />
          <label className={labelClasses}>Role details
            <textarea className={`${fieldClasses} mt-1.5`} rows={4} disabled={!canEdit} {...register('roleDetails')} />
            {errors.roleDetails && <p className="mt-1 text-sm text-error-600">{errors.roleDetails.message}</p>}
          </label>
          <Input id="linkedInUrl" label="LinkedIn URL" disabled={!canEdit} error={errors.linkedInUrl?.message} {...register('linkedInUrl')} />
          <label className={labelClasses}>Additional notes
            <textarea className={`${fieldClasses} mt-1.5`} rows={3} disabled={!canEdit} {...register('additionalNotes')} />
          </label>
          {item.meetingLink && item.status !== 'Completed' && (
            <a href={item.meetingLink} target="_blank" rel="noreferrer" className="inline-flex">
              <Button type="button">Join meeting</Button>
            </a>
          )}
          {canEdit && <Button type="submit" isLoading={saving}>Save interview details</Button>}
          {!canEdit && <p className="text-sm text-gray-500">This interview is {item.status.toLowerCase()} and can no longer be edited.</p>}
        </form>
      </Card>

      {isAdminEditor && item.status === 'Interested' && (
        <Card className="mt-6 p-6 space-y-3">
          <h2 className="text-xl font-semibold text-gray-900">Interest queue</h2>
          <p className="text-sm text-gray-600">This candidate is waitlisted. Move them to Pending when a seat is available.</p>
          <Button
            type="button"
            isLoading={saving}
            onClick={() => {
              setSaving(true);
              setError(null);
              interviewApi.acceptInterest(item.id)
                .then(async (r) => {
                  setMessage(r.data.message || 'Moved to Pending.');
                  await reload();
                })
                .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Unable to accept interest.'))
                .finally(() => setSaving(false));
            }}
          >
            Move to pending
          </Button>
        </Card>
      )}

      {isAdminEditor && item.status !== 'Cancelled' && item.status !== 'Interested' && (
        <Card className="mt-6 p-6 space-y-4">
          <h2 className="text-xl font-semibold text-gray-900">Admin actions</h2>
          <label className="block text-sm font-semibold text-gray-900">Assign interviewer
            <select
              className={`${fieldClasses} mt-1.5`}
              value={item.interviewerId || ''}
              onChange={(e) => {
                const interviewerId = Number(e.target.value);
                if (interviewerId) {
                  interviewApi.assign(item.id, interviewerId, user?.email).then(reload).catch((err: unknown) => setError(err instanceof Error ? err.message : 'Unable to assign.'));
                }
              }}
            >
              <option value="">Select interviewer</option>
              {interviewers.map((person) => (
                <option key={person.interviewerId} value={person.interviewerId}>{person.fullName}</option>
              ))}
            </select>
          </label>
          <Input label="Meeting link" value={meetingLink} onChange={(e) => setMeetingLink(e.target.value)} />
          <div className="grid sm:grid-cols-2 gap-3">
            <Input type="date" label="Scheduled date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} />
            <Input type="time" label="Scheduled time" value={scheduledTime} onChange={(e) => setScheduledTime(e.target.value)} />
          </div>
          <Button
            type="button"
            isLoading={saving}
            onClick={() => {
              setSaving(true);
              interviewApi.schedule(item.id, { meetingLink, scheduledDate, scheduledTime, userEmail: user?.email })
                .then(async () => { setMessage('Schedule saved.'); await reload(); })
                .catch((err: unknown) => setError(err instanceof Error ? err.message : 'Unable to schedule.'))
                .finally(() => setSaving(false));
            }}
          >
            {item.status === 'Scheduled' || item.status === 'Completed' ? 'Reschedule' : 'Schedule interview'}
          </Button>
          <Button
            type="button"
            variant="outline"
            className="text-red-700 border-red-200"
            onClick={async () => {
              const ok = await confirmAction({
                title: `Delete interview #${item.id}?`,
                description: 'This cannot be undone. The candidate will be notified.',
                confirmLabel: 'Delete',
                cancelLabel: 'Keep interview',
                variant: 'danger',
              });
              if (!ok) return;
              interviewApi.delete(item.id, user?.email).then(() => navigate('/admin'));
            }}
          >
            Delete interview
          </Button>
        </Card>
      )}

      {(canFeedback || item.feedback || isOwnerOfRequest) && (
        <Card className="mt-6 p-6">
          <h2 className="text-xl font-semibold text-gray-900">Review feedback</h2>
          <textarea
            className={`${fieldClasses} mt-3`}
            rows={6}
            value={feedback}
            disabled={!canFeedback || (item.status === 'Completed' && !isAdminEditor)}
            onChange={(e) => setFeedback(e.target.value)}
          />
          {canFeedback && (item.status !== 'Completed' || isAdminEditor) && (
            <div className="mt-4 flex flex-wrap gap-3">
              <Button type="button" variant="outline" isLoading={saving} onClick={() => onFeedback(false)}>Save feedback</Button>
              {item.status !== 'Completed' && (
                <Button type="button" isLoading={saving} onClick={() => onFeedback(true)}>Mark as completed</Button>
              )}
            </div>
          )}
        </Card>
      )}
      </div>
    </div>
  );
};

export default InterviewDetailPage;
